import {
  Alert,
  AlertIcon,
  Center,
  Heading,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { LibraryElementTypeDto, useListLibraryElementsQuery } from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { isOwnerOrAdmin } from "../../../common/auth/utils";
import { useApiError } from "../../../common/errors/useApiError";
import { useProcurementTerms } from "../../../common/terminology/useProcurementTerms";
import { AwardCriterionLibraryTab } from "./AwardCriterionLibraryTab";
import { AwardSubCriterionLibraryTab } from "./AwardSubCriterionLibraryTab";
import { MinimumRequirementLibraryTab } from "./MinimumRequirementLibraryTab";
import { QualificationLibraryTab } from "./QualificationLibraryTab";

export const Kravbibliotek = () => {
  const { t } = useTranslation();
  const terms = useProcurementTerms();
  const authState = useLoggedInWithOrgContextState();
  const orgName = authState.selectedOrg.name;
  const canManageOrg = isOwnerOrAdmin(authState.selectedOrg.roles);
  const isSystemAdmin = authState.me.isSystemAdmin;
  const { triggerSoft } = useApiError();
  const { data, isLoading, error } = useListLibraryElementsQuery({});
  const elements = data?.elements ?? [];
  useEffect(() => {
    if (error) triggerSoft(error);
  }, [error, triggerSoft]);
  const countOf = (type: LibraryElementTypeDto) => elements.filter((e) => e.content.type === type).length;

  return (
    <VStack align="stretch" spacing="5">
      <Heading size="lg" color="smText">
        {t("Requirement library")}
      </Heading>
      <Alert status="info" borderRadius="lg" alignItems="flex-start">
        <AlertIcon />
        <Text fontSize="sm">
          {t(
            "Reusable single requirements. The SourceMagnet standard requirements are shared across organizations as a starting point that must be assessed and adapted (Public Procurement Act § 4). The library feeds template configuration, the template picker and search.",
          )}
        </Text>
      </Alert>
      {isLoading && !data ? (
        <Center py="20">
          <Spinner color="smPrimary" />
        </Center>
      ) : (
        <Tabs variant="soft-rounded" colorScheme="teal" isLazy>
          <TabList gap="2">
            <Tab>{`${t("Minimum requirements")} (${countOf(LibraryElementTypeDto.Minimum)})`}</Tab>
            <Tab>{`${t("Qualification requirements")} (${countOf(LibraryElementTypeDto.Selection)})`}</Tab>
            <Tab>{`${terms.awardCriteria} (${countOf(LibraryElementTypeDto.Award)})`}</Tab>
            <Tab>{`${terms.awardSubCriteria} (${countOf(LibraryElementTypeDto.AwardSubCriterion)})`}</Tab>
          </TabList>
          <TabPanels>
            <TabPanel px="0" pt="5">
              <MinimumRequirementLibraryTab
                elements={elements}
                orgName={orgName}
                canManageOrg={canManageOrg}
                isSystemAdmin={isSystemAdmin}
              />
            </TabPanel>
            <TabPanel px="0" pt="5">
              <QualificationLibraryTab
                elements={elements}
                orgName={orgName}
                canManageOrg={canManageOrg}
                isSystemAdmin={isSystemAdmin}
              />
            </TabPanel>
            <TabPanel px="0" pt="5">
              <AwardCriterionLibraryTab
                elements={elements}
                orgName={orgName}
                canManageOrg={canManageOrg}
                isSystemAdmin={isSystemAdmin}
              />
            </TabPanel>
            <TabPanel px="0" pt="5">
              <AwardSubCriterionLibraryTab
                elements={elements}
                orgName={orgName}
                canManageOrg={canManageOrg}
                isSystemAdmin={isSystemAdmin}
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      )}
    </VStack>
  );
};
